import React from 'react';
import {
  formatOperationalTolerance,
  formatOperationalToleranceCounterDisplay,
  hasOperationalToleranceCounter,
  isValidOperationalToleranceValue,
  resolveOperationalToleranceOption,
} from '../counter';
import { COUNTER_ACCENT, COUNTER_HIGHLIGHT_BG } from '../constants';

const counterValueStyle = {
  backgroundColor: COUNTER_HIGHLIGHT_BG,
  color: COUNTER_ACCENT,
  fontWeight: 600,
};

const resolvePartyType = procurement => {
  if (procurement?.partyType == null) return null;
  const text = String(procurement.partyType).trim();
  return text !== '' ? text : null;
};

const toStackedParts = (tolerance, option, fallback = '-') => {
  if (tolerance) return { tolerance, option: option || null };
  if (option) return { tolerance: option, option: null };
  return { tolerance: fallback, option: null };
};

const renderStackedTolerance = (tolerance, option, style) => (
  <div
    className='d-flex flex-column gap-0 lh-sm fs-13'
    style={style || undefined}
  >
    <span>{tolerance}</span>
    {option ? <span className='fw-normal fs-12'>{option}</span> : null}
  </div>
);

export const resolveOperationalToleranceSectionValues = procurement => {
  const originTolerance = isValidOperationalToleranceValue(
    procurement?.operationalTolerance
  )
    ? formatOperationalTolerance(procurement.operationalTolerance)
    : null;
  const originOption = resolvePartyType(procurement);
  const initial =
    formatOperationalToleranceCounterDisplay(
      procurement?.operationalTolerance,
      originOption
    ) || '-';

  const hasCounter = hasOperationalToleranceCounter(
    procurement?.operationalToleranceCounter,
    procurement?.operationalToleranceOptionsCounter,
    procurement?.operationalToleranceOptionsCounterLabel
  );
  if (!hasCounter) {
    return {
      initial,
      counter: null,
      originTolerance,
      originOption,
      counterTolerance: null,
      counterOption: null,
    };
  }

  const counterOption = resolveOperationalToleranceOption({
    label: procurement?.operationalToleranceOptionsCounterLabel,
    optionsCounter: procurement?.operationalToleranceOptionsCounter,
  });
  const counterTolerance = isValidOperationalToleranceValue(
    procurement?.operationalToleranceCounter
  )
    ? formatOperationalTolerance(procurement.operationalToleranceCounter)
    : null;
  const counter =
    formatOperationalToleranceCounterDisplay(
      procurement?.operationalToleranceCounter,
      counterOption
    ) || null;

  return {
    initial,
    counter,
    originTolerance,
    originOption,
    counterTolerance,
    counterOption,
  };
};

export const OperationalToleranceSectionValue = ({
  procurement,
  showCounter = true,
  preferCounter = false,
}) => {
  const {
    initial,
    counter,
    originTolerance,
    originOption,
    counterTolerance,
    counterOption,
  } = resolveOperationalToleranceSectionValues(procurement);

  if (preferCounter) {
    const useCounter = Boolean(counter);
    const parts = useCounter
      ? toStackedParts(counterTolerance, counterOption, counter)
      : toStackedParts(originTolerance, originOption, initial);
    return renderStackedTolerance(
      parts.tolerance,
      parts.option,
      useCounter ? counterValueStyle : undefined
    );
  }

  if (!showCounter) {
    const parts = toStackedParts(originTolerance, originOption, initial);
    return renderStackedTolerance(parts.tolerance, parts.option);
  }

  const originParts = toStackedParts(originTolerance, originOption, initial);
  const counterParts = counter
    ? toStackedParts(counterTolerance, counterOption, counter)
    : null;
  return (
    <div className='d-flex flex-column gap-1'>
      {renderStackedTolerance(originParts.tolerance, originParts.option)}
      {counterParts
        ? renderStackedTolerance(
            counterParts.tolerance,
            counterParts.option,
            counterValueStyle
          )
        : null}
    </div>
  );
};

export default OperationalToleranceSectionValue;
